import { Injectable, OnDestroy, inject } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import {
    BehaviorSubject,
    combineLatest,
    debounceTime,
    defer,
    filter,
    from,
    lastValueFrom,
    map,
    Observable,
    of,
    shareReplay,
    startWith,
    Subject,
    take,
    takeUntil,
} from 'rxjs';
import {
    SituationFTSketchMarkingPoint,
    VoucherSequenceInfo,
    VoucherTab,
    VoucherTabs,
} from './voucher-detail-page.types';
import {
    CreateVoucherInput,
    MedicationGridItemObject,
    MedicationGridObject,
    SecondaryDiagnosisObject,
    UpdateVoucherInput,
    VoucherGridItemObject,
    VoucherObject,
    VoucherPrescribedMedicalProcedureUpsertInput,
} from '@it2go/types';
import { VoucherTypeEnum } from '@libs/types/src/common/enum/voucher-type.enum';
import { VoucherDetailPageStore } from './voucher-detail-page.store';
import { FieldErrorFactory } from '../../../../shared/service/field-error.service';
import { dateify, dateifyOrNow, getLastDayOfMonth, isLastDayOfMonth } from '../../../../shared/helpers/date.helper';
import { BreadcrumbService } from 'xng-breadcrumb';
import { BreadcrumbsEnum } from '../../../../shared/models/breadcrumbs.enum';
import { updateVisitHistoryData } from 'packages/client/src/app/store/global/global.actions';
import { ActivatedRoute, Router } from '@angular/router';
import { isInvalid } from '../../../../shared/helpers/validate';
import {
    VoucherPrescribedMedicalProcedureObject,
} from '@libs/types/src/services/object-type/voucher/voucher-prescribed-medical-procedure.object';
import { formatDosing } from '../folder-medications-page/folder-medications.pipe';
import { fb, globalStore } from '../../../../shared/shared.global-service';
import { selectFolder } from '../../../store/person/person.selectors';
import { tap } from 'rxjs/operators';
import { contentGlobalSuccess } from '../../../content.error';
import { VoucherDetailPageStore2 } from './voucher-detail-page.store2';
import { AppFormGroup } from '../../../../../utils/form.utils';
import { FilterBuilder } from '@libs/shared/utils/grid.builder';
import { getVoucherDetailFrag } from './voucher-detail-page.query';
import { isDiagnosisBonified } from './voucher-detail-page.utils';

const FORM_GROUP_KEYS = {
    general: 'general',
    situationDP: 'situationDP',
    situationORP: 'situationORP',
    situationFT: 'situationFT',
    prescriptions: 'prescriptions',
} as const;

type FormGroupKeys = keyof typeof FORM_GROUP_KEYS;

const FORM_INVALID_STATE = 'INVALID' as const;

export function generalDatesValidator(translate: TranslateService): ValidatorFn {
    return (form: AbstractControl): ValidationErrors | null => {
        const issueDate = form.get('issueDate');
        const issueDateVal = issueDate?.value;
        const validTo = form.get('validTo');
        const validToVal = validTo?.value;
        const closureDate = form.get('closureDate');
        const closureDateVal = closureDate?.value;

        if (!issueDateVal) {
            issueDate?.setErrors(FieldErrorFactory.requiredError());
        }

        if (!validToVal) {
            validTo?.setErrors(FieldErrorFactory.requiredError());
        }

        const issue = new Date(issueDateVal);
        const valid = new Date(validToVal);
        const maxValidDate = new Date(issue);
        maxValidDate.setMonth(maxValidDate.getMonth() + 3); // 3 months from issueDate

        if (issue >= valid) {
            const dateTranslate = translate.instant('validators.params.validity');
            validTo?.setErrors(FieldErrorFactory.invalidValidityPeriodError(dateTranslate));
        } else {
            const errors = validTo?.errors;
            if (errors) {
                delete errors.invalidValidityPeriodError;
                validTo.setErrors(Object.keys(errors).length ? errors : null);
            }
        }

        if (valid > maxValidDate) {
            validTo?.setErrors(FieldErrorFactory.maxVoucherValidityError());
        } else {
            const errors = validTo?.errors;
            if (errors) {
                delete errors.maxVoucherValidity;
                validTo.setErrors(Object.keys(errors).length ? errors : null);
            }
        }

        if (closureDate) {
            if (!closureDateVal) {
                closureDate?.setErrors(null);
                return null;
            }

            const closure = new Date(closureDateVal);

            if (closure <= issue || closure >= valid) {
                closureDate?.setErrors(FieldErrorFactory.closureDateError());
            } else {
                const errors = closureDate?.errors;
                if (errors) {
                    delete errors.closureDateInvalid;
                    closureDate.setErrors(Object.keys(errors).length ? errors : null);
                }
            }
        }

        return null;
    };
}

@Injectable()
export class VoucherDetailService implements OnDestroy {
    protected readonly store = inject(VoucherDetailPageStore);
    protected readonly store2 = inject(VoucherDetailPageStore2);
    private readonly translateService = inject(TranslateService);
    private readonly fb = inject(FormBuilder);
    public readonly breadcrumbService = inject(BreadcrumbService);
    private router = inject(Router);
    private route = inject(ActivatedRoute);

    private translate = (str: string) =>
        this.translateService.instant(`voucher.${str}`);

    public readonly viewTabs = VoucherTab;
    public readonly tabs: VoucherTabs[] = [
        { Id: VoucherTab.general, Label: this.translate('tabs.general') },
        { Id: VoucherTab.situationDP, Label: this.translate('tabs.situation') },
        {
            Id: VoucherTab.situationORP,
            Label: this.translate('tabs.situation'),
        },
        { Id: VoucherTab.situationFT, Label: this.translate('tabs.situation') },
        {
            Id: VoucherTab.prescriptions,
            Label: this.translate('tabs.prescriptions'),
        },
    ];

    private readonly voucher$: BehaviorSubject<VoucherGridItemObject | null> =
        new BehaviorSubject<VoucherGridItemObject | null>(null);

    private _voucherType: VoucherTypeEnum = VoucherTypeEnum.DP;

    private form!: FormGroup;

    private readonly ends$ = new Subject();

    constructor() {
        this.createForm();
    }

    //#region Voucher

    /**
     * Returns voucher model observable.
     *
     * @returns Observable of VoucherGridItemObject model.
     */
    voucher(): Observable<VoucherGridItemObject | null> {
        return this.voucher$;
    }

    /**
     * @Deprecated() voucher().promise()
     * Returns voucher model observable only last.
     *
     * @returns Observable of VOucherObject model.
     */
    voucherOnce(): Observable<VoucherGridItemObject | null> {
        return this.voucher().pipe(take(1));
    }

    /**
     * Returns voucher type observable.
     *
     * @returns Observable of VoucherTypeEnum.
     */
    voucherType(): Observable<VoucherTypeEnum> {
        return of(this._voucherType);
    }

    getVoucherType(): VoucherTypeEnum {
        return this._voucherType;
    }

    /**
     * New voucher model.
     *
     * @param voucherType - voucher type.
     */
    newVoucher(voucherType: VoucherTypeEnum): Observable<VoucherObject | null> {
        this.setBreadcrumb(1);
        this.store.setVoucher(null);
        this._voucherType = voucherType;
        this.voucher$.next(null);
        return of(null);
    }

    /**
     * Loads voucher model.
     *
     * @param voucherId - voucher id.
     */
    loadVoucher(voucherId: number): Observable<VoucherGridItemObject | null> {
        // Clear current voucher
        this.voucher$.next(null);
        const procFilter = new FilterBuilder({ limit: 999 });

        const voucherQuery$ = from(this.store2.api.getVouchers(
            FilterBuilder.filter({ id: voucherId }),
            `
                ${getVoucherDetailFrag.replace('__PRESCRIBED_PROCEDURES_INPUT__', procFilter.filterStr())}
                isBilled
            `,
        ));

        return combineLatest([
            voucherQuery$,
            this.store.serviceExpertise$.pipe(
                filter((expertise) => expertise > 0),
                take(1),
            ),
        ]).pipe(
            map(([vouchers, expertise]) => {
                const voucher = (vouchers as any)?.['items']?.[0] as VoucherGridItemObject;
                if (voucher) {
                    this.setBreadcrumb(voucher.sequenceNumber);
                    this.updateVoucherAndForm(voucher, expertise);
                }
                return voucher;
            }),
        );
    }

    /**
     * Update Voucher and Form - after reload patch from and voucher
     * @param voucher
     * @param expertise
     */
    private updateVoucherAndForm(voucher: VoucherGridItemObject, expertise: number) {
        this.patchForm(voucher, expertise);
        this._voucherType = voucher.type as VoucherTypeEnum;
        this.voucher$.next(voucher);
    }

    async saveVoucher() {
        if (isInvalid(this.form)) {
            return;
        }

        const voucher = await this.voucherOnce().promise();
        const folderId = await this.store.folderId$.promise();

        const body = this.createVoucherInputBody();

        if (voucher) {
            const updateBody: UpdateVoucherInput = {
                ...body,
                voucherId: voucher.id,
            };
            await this.store.svc.updateVoucher(updateBody).promise();
            contentGlobalSuccess.next('common.updated');
        } else {
            const createBody: CreateVoucherInput = {
                ...body,
                type: this._voucherType,
                folderId: folderId || 0,
            };
            await this.store.svc.createVoucher(createBody).promise();
            contentGlobalSuccess.next('common.created');
        }
        await this.router.navigate(['../'], { relativeTo: this.route });
    }

    private setBreadcrumb(seqNumber: number) {
        this.breadcrumbService.set(
            '@' + BreadcrumbsEnum.FolderVoucher,
            BreadcrumbsEnum.FolderVoucher + '|' + seqNumber,
        );
        this.store.globalStore?.dispatch(updateVisitHistoryData());
    }

    /**
     * Create input for create/update entry in DB.
     * @returns
     */
    private createVoucherInputBody(): CreateVoucherInput | UpdateVoucherInput {
        const from = this.generalForm.value.issueDate;
        if (from) from.setHours(0, 0, 0, 0);
        const to = this.generalForm.value.validTo;
        if (to) to.setHours(23, 59, 59, 999);
        const closure = this.generalForm.value.closureDate;
        if (closure) closure.setHours(23, 59, 59, 999);

        return <CreateVoucherInput | UpdateVoucherInput>{
            insuranceNumber: this.generalForm.getRawValue().insuranceNumber || '',
            insuranceAbbr: this.generalForm.value.insuranceAbbr || 0,
            insuranceType: this.generalForm.value.insuranceType || '',
            insuredSince:
                this.generalForm.value.insuredSince?.toISOString() || null,
            sequenceNumber: Number(this.generalForm.value.sequenceNumber || 1),
            voucherPrevId: this.generalForm.value.voucherPrevId,
            issueDate: from?.toISOString() || null,
            validTo: to?.toISOString() || null,
            closureDate:
                closure?.toISOString() || null,
            primaryDiagnosis:
                this.generalForm.value.primaryDiagnosisCode || null,
            compensation: this.generalForm.value.compensationCode || null,
            note: this.generalForm.value.internNote || null,
            doctorId: this.generalForm.getRawValue().doctorId || null,
            secondaryDiagnosis: (
                this.generalForm.value.secondaryDiagnoses || []
            ).map((it: SecondaryDiagnosisObject) => ({
                reporting: it.reporting,
                code: it.diagnosis.code,
            })),
            situation: this.getSituationData(),
            prescribedProcedures: (this.prescriptionsForm.value.prescriptions || []).map((it: VoucherPrescribedMedicalProcedureObject) => (<VoucherPrescribedMedicalProcedureUpsertInput>{
                id: it.id,
                serviceMedicalProcedureId: it.serviceMedicalProcedureId,
                code: it.code,
                canBeBilled: it.canBeBilled,
                canBeBilledWeekend: it.canBeBilledWeekend,
                canBeBilledNight: it.canBeBilledNight,
                continuousOperation: it.continuousOperation,
                courseDescription: it.courseDescription,
                recordTemplate: it.recordTemplate,
                usualNumberOfApplications: it.usualNumberOfApplications,
                frequency: it.frequency,
            })),
        };
    }

    private getSituationData(): string | null {
        switch (this._voucherType) {
            case VoucherTypeEnum.DP:
                return JSON.stringify({
                    anotherInhabitants:
                        this.situationDPForm.value.anotherInhabitants ?? false,
                    inCare: this.situationDPForm.value.inCare ?? false,
                    mobility: this.situationDPForm.value.mobility ?? false,
                    senseDisability:
                        this.situationDPForm.value.senseDisability ?? false,
                    selfServiceAbility:
                        this.situationDPForm.value.selfServiceAbility ?? false,
                    mobilityDesc:
                        this.situationDPForm.value.mobilityDesc || null,
                    senseDisabilityDesc:
                        this.situationDPForm.value.senseDisabilityDesc || null,
                    selfServiceAbilityDesc:
                        this.situationDPForm.value.selfServiceAbilityDesc ||
                        null,
                    medication: this.situationDPForm.value.medication || null,
                    info: this.situationDPForm.value.info || null,
                    goal: this.situationDPForm.value.goal || null,
                    contacts: this.situationDPForm.value.contacts || [],
                });
            case VoucherTypeEnum.ORP:
                return JSON.stringify({
                    dependencyLevel:
                        this.situationORPForm.value.dependencyLevel ?? null,
                    mobilityLevel:
                        this.situationORPForm.value.mobilityLevel ?? null,
                    sensoryDisability:
                        this.situationORPForm.value.sensoryDisability ?? false,
                    selfCareLevel:
                        this.situationORPForm.value.selfCareLevel ?? null,
                    mobilityDescription:
                        this.situationORPForm.value.mobilityDescription ?? null,
                    sensoryDisabilityDescription:
                        this.situationORPForm.value
                            .sensoryDisabilityDescription || null,
                    selfCareDescription:
                        this.situationORPForm.value.selfCareDescription || null,
                    medicationInformation:
                        this.situationORPForm.value.medicationInformation ||
                        null,
                    foodServing:
                        this.situationORPForm.value.foodServing || null,
                    otherInformation:
                        this.situationORPForm.value.otherInformation || null,
                    goal: this.situationORPForm.value.goal || null,
                });
            case VoucherTypeEnum.FT:
                return JSON.stringify({
                    medicalExaminationDate:
                        dateify(
                            this.situationFTForm.value.medicalExaminationDate,
                        ) ?? null,
                    conditionRequiringTreatment:
                        this.situationFTForm.value
                            .conditionRequiringTreatment ?? null,
                    patientRisks:
                        this.situationFTForm.value.patientRisks ?? false,
                    goal: this.situationFTForm.value.goal ?? null,
                    sketchDescription:
                        this.situationFTForm.value.sketchDescription ?? null,
                    sketchMarking:
                        this.situationFTForm.value.sketchMarking || null,
                });
            default:
                return null;
        }
    }

    //#endregion

    //#region navigation

    /**
     * Retrieves the voucher detail tabs by voucher type.
     *
     * @returns An array of VoucherTab.
     */
    getTabs(type: VoucherTypeEnum): VoucherTabs[] {
        switch (type) {
            case VoucherTypeEnum.DP:
                return (
                    this.tabs.filter(
                        (x) =>
                            x.Id !== VoucherTab.situationORP &&
                            x.Id !== VoucherTab.situationFT,
                    ) || []
                );
            case VoucherTypeEnum.ORP:
                return (
                    this.tabs.filter(
                        (x) =>
                            x.Id !== VoucherTab.situationDP &&
                            x.Id !== VoucherTab.situationFT,
                    ) || []
                );
            case VoucherTypeEnum.FT:
                return (
                    this.tabs.filter(
                        (x) =>
                            x.Id !== VoucherTab.situationDP &&
                            x.Id !== VoucherTab.situationORP,
                    ) || []
                );
        }
    }

    //#endregion

    //#region Form

    /**
     * Creates internal form for voucher detail.
     */
    private createForm(): void {
        // check now is last day of moth => set issueDate to last day of next month
        const validTo = new Date();
        if (isLastDayOfMonth(validTo)) {
            validTo.setDate(validTo.getDate() + 1);
        }

        // strip actual time from voucher issue date - due to calculate collissions
        const issueDate = new Date();
        issueDate.setHours(0, 0, 0);

        const formGeneral = new AppFormGroup({
                insuranceNumber: fb.control<string | null>(null, Validators.required),
                insuranceAbbr: fb.control<string | null>(null, Validators.required),
                insuranceType: fb.control<string | null>(null, Validators.required),
                insuredSince: fb.control<Date | null>(null, Validators.required),
                sequenceNumber: fb.control<number | null>(1, [Validators.required, Validators.min(1)]),
                voucherPrevId: fb.control<number | null>(null),
                issueDate: fb.control<Date | null>(issueDate),
                validTo: fb.control<Date | null>(getLastDayOfMonth(validTo)),
                closureDate: fb.control<Date | null>(null),
                compensationCode: fb.control<string | null>(null),
                primaryDiagnosisCode: fb.control<string | null>(null, Validators.required),
                addSecondaryDiagnosis: fb.control<string | null>(null),
                secondaryDiagnoses: fb.control<SecondaryDiagnosisObject[]>([]),
                internNote: fb.control<string | null>(null),
                doctorId: fb.control<string | null>(null, Validators.required),
            },
            { validators: generalDatesValidator(this.translateService) },
        );

        const formSituationDP = fb.group({
            anotherInhabitants: fb.control(false),
            inCare: fb.control(false),
            mobility: fb.control(true),
            senseDisability: fb.control(false),
            selfServiceAbility: fb.control(true),
            mobilityDesc: fb.control<string | null>(null),
            senseDisabilityDesc: fb.control<string | null>(null),
            selfServiceAbilityDesc: fb.control<string | null>(null),
            medication: fb.control<string>(''),
            info: fb.control<string>(''),
            goal: fb.control<string>(''),
            contacts: fb.control<unknown[]>([]),
        });

        const formSituationORP = fb.group({
            dependencyLevel: fb.control<string | null>(null),
            mobilityLevel: fb.control<string | null>(null),
            sensoryDisability: fb.control(false),
            selfCareLevel: fb.control<string | null>(null),
            mobilityDescription: fb.control<string | null>(null),
            sensoryDisabilityDescription: fb.control<string | null>(null),
            selfCareDescription: fb.control<string | null>(null),
            medicationInformation: fb.control<string>(''),
            foodServing: fb.control<string>(''),
            otherInformation: fb.control<string>(''),
            goal: fb.control<string>(''),
        });

        const formSituationFT = fb.group({
            medicalExaminationDate: fb.control<string | null>(null),
            conditionRequiringTreatment: fb.control<string>(''),
            patientRisks: fb.control<string>(''),
            goal: fb.control<string>(''),
            sketchDescription: fb.control<string>(''),
            sketchMarking: fb.control<SituationFTSketchMarkingPoint[] | null>(null),
        });

        const formPrescriptions = new AppFormGroup({
            medicalProcedureCode: fb.control<string | null>(null),
            prescriptions: fb.control<
                VoucherPrescribedMedicalProcedureObject[]
            >([]),
        });

        this.form = new AppFormGroup({
            [FORM_GROUP_KEYS.general]: formGeneral,
            [FORM_GROUP_KEYS.situationDP]: formSituationDP,
            [FORM_GROUP_KEYS.situationORP]: formSituationORP,
            [FORM_GROUP_KEYS.situationFT]: formSituationFT,
            [FORM_GROUP_KEYS.prescriptions]: formPrescriptions,
        });

        // For debug purposes
        // this.form.valueChanges.subscribe((changes) =>
        //     console.log('form changes:', this.form, changes),
        // );
    }

    /**
     * Get general form from internal _form
     *
     * @returns FormGroup
     */
    get generalForm(): AppFormGroup {
        return this.form.controls[FORM_GROUP_KEYS.general] as AppFormGroup;
    }

    /**
     * Get situation DP form from internal _form
     *
     * @returns FormGroup
     */
    get situationDPForm() {
        return this.form.controls[FORM_GROUP_KEYS.situationDP] as FormGroup;
    }

    /**
     * Get situation ORP form from internal _form
     *
     * @returns FormGroup
     */
    get situationORPForm() {
        return this.form.controls[FORM_GROUP_KEYS.situationORP] as FormGroup;
    }

    /**
     * Get situation FT form from internal _form
     *
     * @returns FormGroup
     */
    get situationFTForm() {
        return this.form.controls[FORM_GROUP_KEYS.situationFT] as FormGroup;
    }

    /**
     * Get prescriptions form from internal _form
     *
     * @returns FormGroup
     */
    get prescriptionsForm(): AppFormGroup {
        return this.form.controls[FORM_GROUP_KEYS.prescriptions] as AppFormGroup;
    }

    /**
     * patch inner form value
     *
     * @param voucher - voucher object to set
     * @param expertise - service expertise
     */
    private patchForm(voucher: VoucherGridItemObject, expertise: number): void {
        // patch general form
        this.patchGeneralForm({
            insuranceNumber: voucher.insuranceNumber || null,
            insuranceAbbr: voucher.insuranceAbbr || null,
            insuranceType: voucher.insuranceType || null,
            insuredSince: dateify(voucher.insuredSince),
            sequenceNumber: voucher.sequenceNumber || null,
            voucherPrevId: voucher.voucherPrevId,
            issueDate: dateify(voucher.issueDate),
            validTo: dateify(voucher.validTo),
            closureDate: dateify(voucher.closureDate),
            compensationCode: voucher.compensation?.code || null,
            primaryDiagnosisCode: voucher.primaryDiagnosis?.code || null,
            secondaryDiagnoses: (voucher.secondaryDiagnoses || []).map((it) => {
                const bonified = isDiagnosisBonified(
                    it.diagnosis.bonifications,
                    expertise,
                    voucher.issueDate,
                );

                return {
                    ...it,
                    ...({
                        diagnosis: {
                            code: it.diagnosis.code,
                            name: it.diagnosis.name || it.diagnosis.code,
                            bonified: bonified,
                        } as any,
                    } as any),
                };
            }),
            internNote: voucher.note || null,
            doctorId: voucher.doctorId || null,
        });

        // patch situations form - cased by voucher type
        const situationJson = voucher.situation
            ? JSON.parse(voucher.situation!)
            : null;

        switch (voucher.type as VoucherTypeEnum) {
            case VoucherTypeEnum.DP:
                this.patchSituationDPForm({
                    anotherInhabitants:
                        situationJson?.anotherInhabitants ?? false,
                    inCare: situationJson?.inCare ?? false,
                    mobility: situationJson?.mobility ?? true,
                    senseDisability: situationJson?.senseDisability ?? false,
                    selfServiceAbility:
                        situationJson?.selfServiceAbility ?? true,
                    mobilityDesc: situationJson?.mobilityDesc || null,
                    senseDisabilityDesc:
                        situationJson?.senseDisabilityDesc || null,
                    selfServiceAbilityDesc:
                        situationJson?.selfServiceAbilityDesc || null,
                    medication: situationJson?.medication || '',
                    info: situationJson?.info || '',
                    goal: situationJson?.goal || '',
                    contacts: (situationJson?.contacts || []).map(
                        (it: any) => it.id,
                    ),
                });
                break;
            case VoucherTypeEnum.ORP:
                this.patchSituationORPForm({
                    dependencyLevel: situationJson?.dependencyLevel || null,
                    mobilityLevel: situationJson?.mobilityLevel || null,
                    sensoryDisability: situationJson?.sensoryDisability ?? false,
                    selfCareLevel:
                        situationJson?.selfCareLevel || null,
                    mobilityDescription: situationJson?.mobilityDescription || null,
                    sensoryDisabilityDescription:
                        situationJson?.sensoryDisabilityDescription || null,
                    selfCareDescription:
                        situationJson?.selfCareDescription || null,
                    medicationInformation: situationJson?.medicationInformation || '',
                    foodServing: situationJson?.foodServing || '',
                    otherInformation: situationJson?.otherInformation || '',
                    goal: situationJson?.goal || '',
                });
                break;
            case VoucherTypeEnum.FT:
                this.patchSituationFTForm({
                    medicalExaminationDate:
                        dateify(situationJson?.medicalExaminationDate) || null,
                    conditionRequiringTreatment:
                        situationJson?.conditionRequiringTreatment || null,
                    patientRisks: situationJson?.patientRisks || '',
                    goal: situationJson?.goal || '',
                    sketchDescription: situationJson?.sketchDescription || '',
                    sketchMarking: JSON.parse(situationJson?.sketchMarking || '{}'),
                });
                break;
        }

        // patch prescriptions form
        this.patchPrescriptionsForm({ prescriptions: (voucher.prescribedProcedures || []).map((it) => ({ ...it })) });
    }

    /**
     * patch inner general form
     *
     * @param value
     */
    patchGeneralForm(value: any) {
        this.generalForm.patchValue(value);
    }

    /**
     * patch inner situation DP form
     *
     * @param value
     */
    patchSituationDPForm(value: any) {
        this.situationDPForm.patchValue(value);
    }

    /**
     * patch inner situation ORP form
     *
     * @param value
     */
    patchSituationORPForm(value: any) {
        this.situationORPForm.patchValue(value);
    }

    /**
     * patch inner situation FT form
     *
     * @param value
     */
    patchSituationFTForm(value: any) {
        this.situationFTForm.patchValue(value);
    }

    /**
     * patch inner prescriptions form
     *
     * @param value
     */
    patchPrescriptionsForm(value: any) {
        this.prescriptionsForm.patchValue(value);
    }

    //#endregion

    //#region validation

    /**
     * Creates an observable that emits a boolean value indicating the validation status of a form control.
     * @param formKey The key of the form control.
     * @returns An observable that emits `true` if the form control is invalid, otherwise `false`.
     */
    private makeValidationObservable(
        formKey: FormGroupKeys,
    ): Observable<boolean> {
        return defer(() => {
            return this.form.controls[formKey].statusChanges.pipe(
                debounceTime(200),
                startWith(this.form.controls[formKey].status),
                takeUntil(this.ends$),
                map((status) => {
                    const result =
                        this.form.controls[formKey].touched &&
                        status === FORM_INVALID_STATE;
                    return result;
                }),
                shareReplay(1),
            );
        });
    }

    /**
     * add to Tabs observable with invalid Tabs
     *
     * @returns Observable<boolean>
     */
    invalidationInTabs(): VoucherTabs[] {
        const tabs = this.getTabs(this._voucherType);

        tabs.forEach((tab: VoucherTabs) => {
            switch (tab.Id) {
                case VoucherTab.general:
                    tab.isInvalid$ = this.getGeneralValidation();
                    break;
                case VoucherTab.situationDP:
                    tab.isInvalid$ = this.getSituationDPValidation();
                    break;
                case VoucherTab.situationORP:
                    tab.isInvalid$ = this.getSituationORPValidation();
                    break;
                case VoucherTab.situationFT:
                    tab.isInvalid$ = this.getSituationFTValidation();
                    break;
                case VoucherTab.prescriptions:
                    tab.isInvalid$ = this.getPrescriptionsValidation();
                    break;
            }
        });

        return tabs;
    }

    /**
     * Validates form status and emits boolean value with initial validation status upon subscription.
     * Continues to emit status whenever the form status changes after a debounce time of 200 milliseconds.
     *
     * @returns Observable<boolean> - Emits true if the form is invalid, false otherwise.
     */
    getFormValidation(): Observable<boolean> {
        return this.form.statusChanges.pipe(
            takeUntil(this.ends$),
            startWith(this.form.status),
            debounceTime(200),
            map(() => this.form.touched && this.form.invalid),
        );
    }

    /**
     * get general invalidation, return true if invalid
     *
     * @returns Observe general invalidation
     */
    getGeneralValidation(): Observable<boolean> {
        return this.makeValidationObservable(FORM_GROUP_KEYS.general);
    }

    /**
     * get situationDP invalidation, return true if invalid
     *
     * @returns Obeserve situationDP invalidation
     */
    getSituationDPValidation(): Observable<boolean> {
        return this.makeValidationObservable(FORM_GROUP_KEYS.situationDP);
    }

    /**
     * get situationORP invalidation, return true if invalid
     *
     * @returns Obeserve situationORP invalidation
     */
    getSituationORPValidation(): Observable<boolean> {
        return this.makeValidationObservable(FORM_GROUP_KEYS.situationORP);
    }

    /**
     * get situationFT invalidation, return true if invalid
     *
     * @returns Obeserve situationFT invalidation
     */
    getSituationFTValidation(): Observable<boolean> {
        return this.makeValidationObservable(FORM_GROUP_KEYS.situationFT);
    }

    /**
     * get prescriptions invalidation, return true if invalid
     *
     * @returns Observes prescriptions invalidation
     */
    getPrescriptionsValidation(): Observable<boolean> {
        return this.makeValidationObservable(FORM_GROUP_KEYS.prescriptions);
    }

    //#endregion

    //# region General form
    async getLastVoucher(doctorId: string, newIssueDate: Date, editedVoucherId: number | null = null): Promise<VoucherGridItemObject | null> {
        return this.store.getLastVoucher(doctorId, newIssueDate, editedVoucherId);
    }

    async getNextVoucher(doctorId: string, newIssueDate: Date, editedVoucherId: number | null = null): Promise<VoucherGridItemObject | null> {
        return this.store.getNextVoucher(doctorId, newIssueDate, editedVoucherId);
    }

    public getVoucherSequenceInfo(
        newIssueDate: Date,
        newVoucherTo: Date, // closure when not null or validTo
        lastVoucher: VoucherGridItemObject | null, // previously ended voucher
        nextVoucher: VoucherGridItemObject | null = null, // earliest next ending voucher
    ): VoucherSequenceInfo {
        const info: VoucherSequenceInfo = {
            sequenceNumber: null,
            lastVoucherId: null,
        };
        const newFromDate = new Date(newIssueDate);
        newFromDate.setHours(0, 0, 0, 0);
        const newToDate = new Date(newVoucherTo);
        newToDate.setHours(23, 59, 59, 999);

        const lastEndDate = new Date(
            lastVoucher?.closureDate || lastVoucher?.validTo || 0,
        );
        lastEndDate.setDate(lastEndDate.getDate() + 1);
        lastEndDate.setHours(0, 0, 0, 0);

        const nextIssueDate = new Date(nextVoucher?.issueDate || 0);
        nextIssueDate.setHours(0, 0, 0, 0);
        const nextToDate = new Date(nextVoucher?.closureDate || nextVoucher?.validTo || 0);
        nextToDate.setHours(23, 59, 59, 999);

        if (
            lastVoucher &&
            lastEndDate.getTime() === newFromDate.getTime() &&
            (!nextVoucher ||
                (nextIssueDate.getTime() > newToDate.getTime() || nextToDate.getTime() < newFromDate.getTime())
            )
        ) {
            // continuing sequence
            info.sequenceNumber = lastVoucher.sequenceNumber + 1;
            info.lastVoucherId = lastVoucher.id;
        } else {
            if (
                nextVoucher &&
                nextIssueDate.getTime() < newToDate.getTime() &&
                nextToDate.getTime() > newFromDate.getTime()
            ) {
                info.sequenceNumber = null; // overlaping dates
            } else {
                info.sequenceNumber = 1; // new sequence (in between dates)
            }
        }

        return info;
    }

    //#endregion

    public async formatActualMedicationsText() {
        const folder = globalStore.selectSignal(selectFolder);
        if (!folder()) return '';
        const voucherFrom = dateifyOrNow(this.generalForm.value['issueDate']);
        const voucherTo = dateify(this.generalForm.value['closureDate']) || dateifyOrNow(this.generalForm.value['validTo']);
        const now = new Date();
        const today = new Date(now.getTime());
        today.setHours(0, 0, 0, 0);
        const medications = (folder()?.folderMedications || []).filter((it) => {
            const medFrom = new Date(it.from);
            const medTo = it.to ? new Date(it.to) : null;
            return it.active &&
                (medTo === null || medTo > voucherFrom) &&
                medFrom < voucherTo
                && (!this.generalForm.value.doctorId || !it.prescribedBy || this.generalForm.value.doctorId === it.prescribedBy)
                ;
        });
        // Fetch names based on codes
        const names: Record<string, string> = {};
        const codes = new Set<string>();
        medications.filter((it) => {
            if (it.customMedication) return;
            codes.add(it.medicationCode);
        });

        if (codes.size) {
            this.store.api.getMedications(FilterBuilder.singlePage({
                code_IN: Array.from(codes),
            }));
            await lastValueFrom(
                this.store.apiSuccess.getMedications.pipe(take(1)).pipe(tap((medications: MedicationGridObject) => {
                        medications?.['items'].forEach((it: MedicationGridItemObject) => {
                            names[it.code] = `${it.name}, ${it.nameAddition}`;
                        });
                    }),
                ),
            );
        }

        let data = medications.map((it) => `${formatDosing(it.dosingPeriod, it.dosing, this.store2.currentService()?.timeRanges)} ${names[it.medicationCode]}`).join('<br />');
        if (data) {
            data = `${this.translateService.instant('voucher.messages.yesMedication')}:<br />${data}`;
        } else {
            data = this.translateService.instant('voucher.messages.noMedication');
        }

        return data;
    }

    ngOnDestroy(): void {
        this.ends$.next(null);
        this.ends$.complete();
    }
}
